import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import endpoints from '../constants/endpoints';
import {
	AutocompleteContactData,
	OptionalService,
	QuotationInfoToSendClient,
	QuotationInfoToSendClientResponse,
	QuotationSnapShotToClient,
	QuotationSnapShotToClientDestinationMultiple,
	QuotationSnapShotToClientOnlyFlights,
	ServiceOption,
} from '../features/quotation/types';
import { CookieService, SessionService } from 'features/auth/services/CookieService';
import { HotelSuggestion } from '../features/quotation/components/HotelOptionsConfig';
import { defaultCountryWithAgencyConfig } from '../features/auth/constants';
import { ICodeName } from 'features/salesOrder/types';
import { resolveApiKeyForAgency } from './agencyRequest';

export const ApiService = axios.create({
	headers: {
		'Content-Type': 'application/json',
	},
	baseURL: process.env.REACT_APP_API_URL,
});

export const ApiSaleOrderService = axios.create({
	headers: {
		'Content-Type': 'application/json',
	},
	baseURL: process.env.REACT_APP_SALES_ORDER_API_URL,
});

export const ApiHotelProxyService = axios.create({
	headers: {
		'Content-Type': 'application/json',
	},
	baseURL: process.env.REACT_APP_HOTEL_PROXY_API_URL,
});

export const ApiMeVueloService = axios.create({
	headers: {
		'Content-Type': 'application/json',
	},
	baseURL: process.env.REACT_APP_MEVUELO_API_URL,
});

export const ApiPackageService = axios.create({
	headers: {
		'Content-Type': 'application/json',
	},
	baseURL: process.env.REACT_APP_PACKAGES_API_URL,
});

export const axiosDestinations = axios.create({
	baseURL: process.env.REACT_APP_DESTINATION_CMS_API_URL,
	headers: {
		'Content-Type': 'application/json',
	},
});

export const ApiTravelInfoService = axios.create({
	baseURL: 'https://integrations.mevuelo.com',
	headers: {
		'Content-Type': 'application/json',
	},
});

export const ApiSalesDashboardService = axios.create({
	baseURL: process.env.REACT_APP_SUPPORT_API_BASE_URL,
	headers: {
		'Content-Type': 'application/json',
	},
});

// Cliente dedicado al módulo de Paquetes (Producto) de la Support API.
// Misma base que el dashboard de ventas, pero instancia separada para no
// mezclar interceptores ni configuraciones. NO confundir con ApiPackageService
// (que apunta a la API de paquetes legacy, REACT_APP_PACKAGES_API_URL).
export const ApiPackagesSupportService = axios.create({
	baseURL: process.env.REACT_APP_SUPPORT_API_BASE_URL,
	headers: {
		'Content-Type': 'application/json',
	},
});

// Cliente dedicado a los endpoints de cotizaciones de la Support API
// (get-vouchers-api). Instancia separada por la misma razón que las de arriba:
// no mezclar interceptores ni configuración entre módulos.
export const ApiQuotationsSupportService = axios.create({
	baseURL: process.env.REACT_APP_SUPPORT_API_BASE_URL,
	headers: {
		'Content-Type': 'application/json',
	},
});

ApiTravelInfoService.interceptors.request.use((config) => {
	const authToken = CookieService.getToken();
	const apiKey = SessionService.getApiKey();
	const isProduction = window.location.href.includes('https://power.mevuelo.com');
	const env = isProduction ? 'prod' : 'test';

	config.headers['X-Auth-Token'] = authToken;
	config.headers['X-api-key'] = apiKey;
	config.headers['xenv'] = env;

	return config;
});

ApiSaleOrderService.interceptors.request.use((config) => {
	const agencyApiKey = resolveApiKeyForAgency(config.agencyId);
	if (agencyApiKey) {
		config.headers['X-Api-Key'] = agencyApiKey;
	} else {
		const maybeApiKey = SessionService.getApiKey();
		if (maybeApiKey) config.headers['X-Api-Key'] = maybeApiKey;
		else config.headers['X-Api-Key'] = defaultCountryWithAgencyConfig.apiKey;
	}
	const token = CookieService.getToken();
	if (token) {
		config.headers['Authorization'] = `Bearer ${token}`;
	}
	return config;
});

ApiService.interceptors.request.use((config) => {
	const maybeApiKey = SessionService.getApiKey();
	if (maybeApiKey) config.headers['X-Api-Key'] = maybeApiKey;
	else config.headers['X-Api-Key'] = defaultCountryWithAgencyConfig.apiKey;
	return config;
});

ApiHotelProxyService.interceptors.request.use((config) => {
	const maybeApiKey = SessionService.getApiKey();
	if (maybeApiKey) config.headers['X-Api-Key'] = maybeApiKey;
	else config.headers['X-Api-Key'] = defaultCountryWithAgencyConfig.apiKey;
	return config;
});

ApiPackageService.interceptors.request.use((config) => {
	const maybeApiKey = SessionService.getApiKey();
	if (maybeApiKey) config.headers['X-Api-Key'] = maybeApiKey;
	else config.headers['X-Api-Key'] = defaultCountryWithAgencyConfig.apiKey;
	return config;
});

ApiService.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config;
		if (error.response.status === 401 && !originalRequest._retry) {
			CookieService.clearToken();
			SessionService.clearApiKey();
			location.reload();
		} else throw error;
	},
);

ApiHotelProxyService.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config;
		if (error.response.status === 401 && !originalRequest._retry) {
			CookieService.clearToken();
			SessionService.clearApiKey();
			location.reload();
		}
	},
);

ApiPackageService.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config;
		if (error.response.status === 401 && !originalRequest._retry) {
			CookieService.clearToken();
			SessionService.clearApiKey();
			location.reload();
		}
	},
);
ApiMeVueloService.interceptors.request.use((config) => {
	const token = CookieService.getMeVueloToken();
	if (token) config.headers['Authorization'] = `Bearer ${token}`;
	return config;
});

/**
 * Valida que una credencial no sea un valor vacío, nulo o los strings literales "undefined"/"null".
 * Se usa en todos los interceptores de Support API y Travel Intelligence.
 */
const isValidCredential = (value: string | null | undefined): value is string => {
	if (!value) return false;
	const trimmed = value.trim();
	return trimmed !== '' && trimmed !== 'undefined' && trimmed !== 'null';
};

/**
 * Agrega X-Api-Key (Q_API_KEY) y X-Auth-Token (Q_TOKEN) a cada request de Support API.
 * Reutiliza las mismas credenciales de sesión que el resto del ecosistema.
 * Si alguna credencial está ausente o es inválida, cancela el request en lugar de enviarlo incompleto.
 */
const attachSupportApiCredentials = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
	const apiKey = SessionService.getApiKey();
	const token = CookieService.getToken();

	if (!isValidCredential(apiKey)) {
		throw new Error('Support API: API key de sesión no disponible. Iniciá sesión nuevamente.');
	}
	if (!isValidCredential(token)) {
		throw new Error('Support API: token de sesión no disponible. Iniciá sesión nuevamente.');
	}

	config.headers['X-Api-Key'] = apiKey;
	config.headers['X-Auth-Token'] = token;

	return config;
};

ApiSalesDashboardService.interceptors.request.use(attachSupportApiCredentials);

ApiPackagesSupportService.interceptors.request.use(attachSupportApiCredentials);

ApiQuotationsSupportService.interceptors.request.use(attachSupportApiCredentials);

ApiQuotationsSupportService.interceptors.response.use(
	(response) => response,
	// Un 401/403 acá no debe desloguear al usuario: lo maneja quien llama.
	(error) => Promise.reject(error),
);

ApiPackagesSupportService.interceptors.response.use(
	(response) => response,
	(error) => {
		// Un 401/403 de la Support API no debe desloguear al usuario de toda la app:
		// cada hook captura el error y lo muestra en su sección.
		return Promise.reject(error);
	},
);

ApiSalesDashboardService.interceptors.response.use(
	(response) => response,
	(error) => {
		// No se redirige al login: un 401 en los endpoints del dashboard no debe
		// desloguear al usuario de toda la app. El hook que llama a este servicio
		// captura el error y lo muestra en su sección correspondiente.
		return Promise.reject(error);
	},
);

export const fetcher = (url: string): Promise<AxiosResponse> => ApiService.get(url).then((res) => res.data);

export const fetcherPost = (url: string, payload: unknown): Promise<any> =>
	ApiService.post(url, payload).then((res) => res.data);

export const parseGDSAirRawData = async (gdsRawData: string): Promise<AxiosResponse> =>
	ApiService.post(endpoints.airParser, gdsRawData, {
		headers: { 'Content-Type': 'text/plain' },
	});

export const destinationSuggestion = async (value: string): Promise<AxiosResponse> =>
	ApiService.get(`${endpoints.destinationSuggestion}${value}`);

export const getOptionalServicesByDestination = async (cityCode: string): Promise<AxiosResponse<OptionalService[]>> =>
	ApiService.get(`${endpoints.optionalServicesByDestination.replace('[cityCode]', cityCode)}`);

export const hotelSuggestion = async (
	cityCode: string,
	queryText: string,
): Promise<AxiosResponse<HotelSuggestion[]>> => {
	const queryTextEncode = encodeURIComponent(queryText);
	const finalEndpoint = endpoints.hotelSuggestion
		.replace('[cityCode]', cityCode)
		.replace('[queryText]', queryTextEncode);
	return ApiService.get(finalEndpoint);
};

export const postServicesSuggestions = async (
	from: string,
	cityCodes: string[],
): Promise<AxiosResponse<ServiceOption[]>> =>
	ApiService.post(endpoints.servicesSuggestion, {
		from,
		destinations: cityCodes,
	});

export const postSendQuotationToClient = async (
	info: QuotationInfoToSendClient,
): Promise<AxiosResponse<QuotationInfoToSendClientResponse>> => ApiService.post(endpoints.quotationToClient, info);

export const findQuotationToClient = async (
	id: string,
): Promise<
	AxiosResponse<
		QuotationSnapShotToClient | QuotationSnapShotToClientDestinationMultiple | QuotationSnapShotToClientOnlyFlights
	>
> => ApiService.get(`${endpoints.quotationToClient}/${id}`);
export const findContactDataByDeal = async (dealId: number): Promise<AxiosResponse<AutocompleteContactData>> =>
	ApiService.get(`${endpoints.autocompleteContact.replace('[dealId]', String(dealId))}`);
export const findContactsDataByDealID = async (dealId: number): Promise<AxiosResponse<AutocompleteContactData[]>> =>
	ApiService.get(`${endpoints.contactsByDealId.replace('[dealId]', String(dealId))}`);

export const fetchAirlineCodes = async (): Promise<AxiosResponse<ICodeName[]>> => ApiService.get(endpoints.getAirlines);
